import { z } from "zod";

/** Payments (chunk D): cards on file, deposits, bills, checkout, refunds. */

export const PAYMENT_METHOD_LABELS = {
  card: "Card",
  card_on_file: "Card on file",
  card_present: "Card reader",
  cash: "Cash",
  account_credit: "Account credit",
} as const;
export type PaymentMethod = keyof typeof PAYMENT_METHOD_LABELS;

export interface SavedCardView {
  id: string;
  brand: string;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
  savedAt: string;
}

export interface PaymentView {
  id: string;
  kind: "deposit" | "checkout" | "fee" | "refund" | "plan";
  method: PaymentMethod;
  status: "pending" | "authorized" | "succeeded" | "failed" | "canceled";
  amountCents: number;
  tipCents: number;
  refundedCents: number;
  cardBrand: string | null;
  cardLast4: string | null;
  payer: string | null;
  reason: string | null;
  failureMessage: string | null;
  by: string | null;
  at: string;
  /** Visit date and service, for lists. */
  visit: { appointmentId: string; date: string; serviceName: string; clientName: string } | null;
}

export interface BillLineView {
  id: string;
  kind: "service" | "add_on" | "product" | "travel" | "fee";
  name: string;
  quantity: number;
  unitCents: number;
  amountCents: number;
  source: "booked" | "chart" | "added";
  /** Paid for by a membership or package voucher (the plan's name). */
  coveredBy: string | null;
  /** Member pricing on this line ("The Vault Membership 20% off"). */
  planDiscountCents: number;
  planDiscountLabel: string | null;
  /** Added at checkout, but the visit's signed chart doesn't have it (the nurse will update the chart). */
  notCharted: boolean;
}

export interface BillView {
  id: string;
  appointmentId: string;
  client: { id: string; name: string };
  serviceName: string;
  start: string;
  status: "open" | "paid" | "void";
  lines: BillLineView[];
  subtotalCents: number;
  discountCents: number;
  discountReason: string | null;
  /** Lines paid for by vouchers. */
  coveredCents: number;
  /** Member pricing on the other lines. */
  planDiscountCents: number;
  /** Member pricing and a manual discount don't stack: which one counts. */
  discountApplied: "plan" | "manual" | null;
  /** Staff took member pricing off, and why. */
  planDiscountOffReason: string | null;
  depositCreditCents: number;
  /** Tips are figured on this: the full amount before discounts and deposits. */
  tipBaseCents: number;
  tipCents: number;
  /** subtotal − covered − (the larger of member pricing and the discount) − deposit credit + tip */
  totalCents: number;
  paidCents: number;
  balanceCents: number;
  nurseId: string | null;
}

/** Everything the checkout screen needs for one visit and the rest of its booking. */
/** A no-show or late-cancel fee that couldn't be charged (no card, or declined): owed until it's added to a bill or waived. */
export interface FeeOwed {
  paymentId: string;
  clientId: string;
  amountCents: number;
  /** "No-show" or "Late cancellation". */
  reason: string;
  /** The visit's date (clinic-local "YYYY-MM-DD"). */
  date: string;
}

export interface CheckoutView {
  appointmentId: string;
  /** Fees owed by the people on these bills (or their bookers). Nothing is added on its own. */
  feesOwed: FeeOwed[];
  /** Where "Text a pay link" starts: the client's mobile (a minor's parent's), else their email. */
  linkTo: string | null;
  /** Account credit per client in the booking (only those with some). */
  credit: Record<string, number>;
  bookingId: string;
  bills: BillView[];
  payments: PaymentView[];
  /** Saved cards per client in the booking. */
  cards: Record<string, SavedCardView[]>;
  tipPercents: number[];
  readerEnabled: boolean;
  /** The way to pay Checkout starts on (Settings → Payments), when it's available. */
  startsOn: "card_on_file" | "card" | "card_present" | "cash" | "link" | "ipad";
  readers: { id: string; label: string }[];
  /** Stripe publishable key; null when payments run in development mode. */
  publishableKey: string | null;
}

export const addBillLineSchema = z.object({
  catalogItemId: z.string().uuid().nullable().default(null),
  name: z.string().trim().max(120).default(""),
  quantity: z.number().int().min(1).max(50).default(1),
  /** Required when there's no catalog item. */
  unitCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
});

export const discountSchema = z.object({
  kind: z.enum(["amount", "percent"]),
  value: z.number().min(0).max(10_000_000),
  reason: z.string().trim().max(300).default(""),
});

export const tipSchema = z.object({ tipCents: z.number().int().min(0).max(1_000_000) });

export const paySchema = z.object({
  billIds: z.array(z.string().uuid()).min(1).max(20),
  method: z.enum(["card_on_file", "card", "card_present", "cash", "account_credit"]),
  savedCardId: z.string().uuid().nullable().default(null),
  readerId: z.string().uuid().nullable().default(null),
  /** Cash: what the client handed over (for change). */
  cashReceivedCents: z.number().int().min(0).max(10_000_000).nullable().default(null),
  /** Card reader with no tip added yet: the client picks one on the reader (set by the server, not the caller). */
  tipOnReader: z.boolean().default(false),
  /** Card reader: keep the card as their card on file (only when the payer has none yet). */
  saveCard: z.boolean().default(true),
});
export type PayInput = z.infer<typeof paySchema>;

export const refundSchema = z.object({
  amountCents: z.number().int().min(1).max(10_000_000),
  reason: z.string().trim().min(1, "Add a reason").max(300),
  /** Give it back as account credit instead of to the card (or cash). */
  toCredit: z.boolean().default(false),
});

export const payLinkSchema = z.object({
  billIds: z.array(z.string().uuid()).min(1).max(20),
  channel: z.enum(["sms", "email"]),
  /** Phone or email to send to (e.g. a parent). */
  to: z.string().trim().min(3).max(254),
  name: z.string().trim().max(120).default(""),
});

/** What a pay/save-card page shows (public, opened from a link). */
export interface PayPageView {
  clinicName: string;
  kind: "pay" | "save_card";
  /** Save-card pages: the page's heading (e.g. "Add a card for your Gold membership"); null for the default. */
  heading?: string | null;
  title: string;
  lines: { label: string; amountCents: number }[];
  totalCents: number;
  tipPercents: number[];
  /** Pay: a tip can still be added (the client is paying their own visit). */
  allowTip: boolean;
  tipBaseCents: number;
  done: boolean;
  publishableKey: string | null;
  /** Save-card links: the policy the client agrees to by saving it. */
  agreement: string | null;
  /** A visit's pay link: the checkout itself (the bill, a tip, the card on file or a new card), like the iPad's. */
  checkout?: RemoteCheckoutView | null;
}

/** Checkout from a texted or emailed link: the client tips and pays on their own phone. */
export interface RemoteCheckoutView {
  bills: Omit<BillView, "nurseId">[];
  tipPercents: number[];
  /** The card on file it can be paid with: only when the link went to that cardholder's own phone or email. */
  card: { brand: string; last4: string } | null;
  /** What's still owed, tips included. */
  dueCents: number;
}

export const remoteTipSchema = z.object({ billId: z.string().uuid(), tipCents: z.number().int().min(0).max(1_000_000) });
export const remotePaySchema = z.object({ method: z.enum(["card_on_file", "card"]) });
/** Paying from the link: a new card opens Stripe's form; a card on file is charged now (or says why not). */
export interface RemotePayResult {
  page: PayPageView;
  session: PaymentSession | null;
  message: string | null;
}

/** Start an embedded Stripe checkout; the web app mounts it with this secret. */
export interface PaymentSession {
  sessionRef: string;
  clientSecret: string;
  publishableKey: string | null;
}

/** Day's payments, for closing out. */
export interface DayPayments {
  date: string;
  payments: PaymentView[];
  totals: { method: PaymentMethod; amountCents: number; tipCents: number; count: number }[];
  refundsCents: number;
  tipsByNurse: { staffId: string; name: string; tipCents: number }[];
}

/** A deposit for a booking, by rule (flat per guest, or percent of the total, with min/max). */
export function depositFor(rule: { mode: "flat_per_guest" | "percent"; flatCents: number; percent: number; minCents: number | null; maxCents: number | null }, guests: number, totalCents: number): number {
  let d = rule.mode === "flat_per_guest" ? rule.flatCents * guests : Math.round((totalCents * rule.percent) / 100);
  if (rule.minCents != null) d = Math.max(d, rule.minCents);
  if (rule.maxCents != null) d = Math.min(d, rule.maxCents);
  return Math.min(Math.max(d, 0), totalCents);
}

/** The wording clients agree to when a clinic booking saves their card. */
export function cardOnFileAgreement(feeCents: number, windowHours: number, format: (c: number) => string, optional = false): string {
  // A card is optional: the fee still applies (taken at the desk when there's no card).
  if (optional) return feeCents > 0 ? `Cancellations within ${windowHours} hours of the appointment, and no-shows, are charged a ${format(feeCents)} fee.` : "";
  return feeCents > 0
    ? `We save your card for this visit. Cancellations within ${windowHours} hours of the appointment, and no-shows, are charged a ${format(feeCents)} fee to this card.`
    : "We save your card for this visit, for checkout.";
}

/** Reports → Sales: sales by paid date (bills closed in the period) and money collected in it. */
export interface SalesReport {
  from: string;
  to: string;
  summary: {
    /** Paid visits (bills closed) in the period. */
    visits: number;
    grossCents: number;
    /** Paid for by membership and package vouchers (sold earlier). */
    coveredCents: number;
    /** Manual discounts and member pricing (the larger on each bill). */
    discountsCents: number;
    /** gross − covered − discounts */
    netCents: number;
    /** Memberships and packages paid in the period. */
    planSalesCents: number;
    tipsCents: number;
    /** Part of net sales paid earlier as booking deposits. */
    depositsAppliedCents: number;
    /** Paid with account credit (not money coming in), and refunds given back as credit. */
    creditUsedCents: number;
    refundsToCreditCents: number;
    /** No-show and late-cancel fees charged. */
    feesCents: number;
    refundsCents: number;
    /** Money taken in the period, by method (refunds not taken out). */
    collected: { method: PaymentMethod; count: number; amountCents: number }[];
    collectedCents: number;
  };
  items: { category: string; name: string; quantity: number; amountCents: number }[];
  nurses: { name: string; visits: number; netCents: number; tipsCents: number }[];
  days: { date: string; visits: number; netCents: number; tipsCents: number; collectedCents: number }[];
  /**
   * Checkouts done in Boulevard (their receipts), closed in the period: only when asked for, and
   * never added into the figures above.
   */
  boulevard: BoulevardSales | null;
}

export interface BoulevardSales {
  /** Visits with a Boulevard checkout closed in the period. */
  visits: number;
  /** Before discounts, and the discounts (tip left out). */
  grossCents: number;
  discountsCents: number;
  netCents: number;
  tipsCents: number;
  taxAndFeesCents: number;
  refundsCents: number;
  /** Paid with a voucher, membership credit, or gift card (bought earlier). */
  prepaidCents: number;
  /** Money taken, by Boulevard's payment type (prepaid ones left out). */
  collected: { label: string; count: number; amountCents: number }[];
  collectedCents: number;
  items: { category: string; name: string; quantity: number; amountCents: number }[];
  days: { date: string; visits: number; netCents: number; tipsCents: number; collectedCents: number }[];
}

/** A client's account credit: the balance, and every change (newest first). */
export interface ClientCreditView {
  balanceCents: number;
  history: { id: string; at: string; kind: "added" | "removed" | "used" | "returned" | "refund"; amountCents: number; reason: string; by: string | null }[];
}

export const creditChangeSchema = z.object({
  /** Positive: add credit; negative: take it off. */
  amountCents: z
    .number()
    .int()
    .min(-10_000_000)
    .max(10_000_000)
    .refine((v) => v !== 0, "Enter an amount"),
  reason: z.string().trim().min(2, "Say why").max(300),
});

/** Another visit today, not paid yet, that can be checked out together with this one. */
export interface CheckoutOther {
  appointmentId: string;
  name: string;
  /** Clinic-local "2026-10-31T14:00". */
  time: string;
  serviceName: string;
}
